import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";

import { ContactQuickLinks } from "@/components/ContactQuickLinks";
import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { RecordPage } from "@/components/record/RecordPage";
import type { RecordField } from "@/components/record/fields";
import type { DataTableColumn } from "@/components/table/columns";
import type { InlineEditCommitArgs } from "@/components/table/use-inline-edits";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { VendorsIcon } from "@/components/ui/icons";
import { ApiError, apiFetch } from "@/lib/api";
import type { Page, ReferenceListItemResource, VendorResource } from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";
import type { EditCommitOutcome, EditCommitPayload } from "@/lib/inline-edit";
import { showToast } from "@/lib/toast-queue";

/**
 * `/vendors/:id` (Story 16.2). Built on `RecordPage`/`FieldRow` — never a
 * parallel page implementation, mirroring `TenantRecord.tsx`'s own rule
 * exactly (spec's own bullet 4). Every `Contact`-owned field
 * (`full_name`/`mobile_phone`/`email`/`preferred_contact_method`) and every
 * `Vendor`-owned field (`trade`/`company_name`/`notes`) is independently
 * inline-editable through the identical `PATCH`/optimistic-cache-write
 * pattern `TenantRecord.tsx` already established (VC-16.2-05) — no new
 * inline-edit machinery invented.
 *
 * **Delete/restore (judgement call 4)** mirrors `TenantRecord.tsx`'s own
 * exact shape, checked directly against that file rather than invented
 * fresh: fields become read-only once archived (`isOwner && !isArchived`
 * gating every `edit` config), a "Restore" `primaryAction` replaces the
 * "Remove" `secondaryActions` entry, and a real `POST /vendors/{id}/restore`
 * call runs on click — no separate "Archived" badge/text renders anywhere,
 * because `TenantRecord.tsx` itself renders none either (confirmed by
 * reading that file directly, not assumed): its own soft-delete indication
 * is the read-only fields plus the Restore action, not a status pill (no
 * `deletion` prop is passed to `RecordPage` there, and no `Badge` renders
 * for `is_archived`) — this page reproduces that same, real shape rather
 * than inventing a badge `TenantRecord.tsx` itself does not have.
 *
 * No key-facts strip, no lease-history/communication-log sections — Vendor
 * has no lease concept and no communication log in this story's own scope
 * (spec's own "Out of scope" list), so this page is genuinely simpler than
 * `TenantRecord.tsx`, not a stripped copy with dead sections left behind.
 * `ContactQuickLinks` (real `tel:`/`sms:`/`mailto:` anchors) is reused
 * as-is — the same one-click contact affordance a vendor's own phone/email
 * are exactly as useful for as a tenant's.
 */

const CONTACT_METHOD_OPTIONS = [
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "text", label: "Text" },
];

/** An emptied text field is sent as `null` — mirrors `TenantRecord.tsx`'s
 * identical `patchBodyFor`: a cleared value returns the field to
 * "unanswered", not to an empty string a `NOT NULL` column would refuse. */
function patchBodyFor(fieldKey: string, payload: EditCommitPayload): Record<string, unknown> {
  if (payload.kind !== "value") {
    throw new Error(`unexpected transition-kind payload for field "${fieldKey}"`);
  }
  return { [fieldKey]: payload.value === "" ? null : payload.value };
}

export function VendorRecord() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  //: `PATCH`/`DELETE`/restore `/vendors/{id}` are all `role=Role.OWNER`
  //: (`app/api/vendors.py`'s own module docstring) — every edit/delete/
  //: restore affordance is withheld from anyone but a confirmed Owner,
  //: mirroring `TenantRecord.tsx`'s identical `isOwner`-gated rule.
  const isOwner = useIsOwner();

  const queryKey = useMemo(() => ["vendors", "record", id] as const, [id]);

  const recordQuery = useQuery({
    queryKey,
    queryFn: () => apiFetch<VendorResource>(`/vendors/${id}`),
    enabled: id !== undefined,
  });

  //: VC-16.2-07 — the same live `vendor_trade` reference list the list
  //: screen's own filter and `AddVendorDialog.tsx` already read, so this
  //: page's own inline `trade` `<select>` is never a second, independently
  //: -stale copy of that vocabulary.
  const tradesQuery = useQuery({
    queryKey: ["reference-list-items", "vendor_trade"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=vendor_trade"),
  });
  const tradeOptions = (tradesQuery.data?.data ?? []).map((option) => ({
    value: option.value,
    label: option.label,
  }));
  //: Mirrors `VendorsList.tsx`'s own identical `tradeLabelByValue` — the
  //: resting cell shows the reference list's own human label ("Electrician"),
  //: never the raw stored value ("electrician"), the same "one field name,
  //: one label, everywhere it appears" discipline `DocumentsList.tsx`'s own
  //: `categoryLabelByValue` already established for `category`.
  const tradeLabelByValue = useMemo(() => {
    const map: Record<string, string> = {};
    for (const option of tradesQuery.data?.data ?? []) {
      map[option.value] = option.label;
    }
    return map;
  }, [tradesQuery.data]);

  useEffect(() => {
    document.title = recordQuery.data ? `${recordQuery.data.full_name} — PropDesk` : "Vendor — PropDesk";
  }, [recordQuery.data]);

  function writeRecordToCache(updated: VendorResource) {
    queryClient.setQueryData<VendorResource>(queryKey, updated);
  }

  async function sendPatch(
    row: VendorResource,
    field: DataTableColumn<VendorResource>,
    payload: EditCommitPayload,
  ): Promise<EditCommitOutcome> {
    try {
      const updated = await apiFetch<VendorResource>(`/vendors/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patchBodyFor(field.key, payload)),
      });
      writeRecordToCache(updated);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  async function commitEdit(args: InlineEditCommitArgs<VendorResource>): Promise<EditCommitOutcome> {
    return sendPatch(args.row, args.column, args.payload);
  }

  const [removeOpen, setRemoveOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | undefined>(undefined);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement>(null);

  async function confirmRemove() {
    if (id === undefined || recordQuery.data === undefined) {
      return;
    }
    setIsRemoving(true);
    setRemoveError(undefined);
    try {
      await apiFetch<{ deleted: boolean }>(`/vendors/${id}`, { method: "DELETE" });
      writeRecordToCache({ ...recordQuery.data, is_archived: true });
      setRemoveOpen(false);
    } catch (error) {
      setRemoveError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsRemoving(false);
    }
  }

  /** Recovery, not a destructive action — no confirmation step, mirroring
   * `TenantRecord.tsx`'s identical un-archive/restore shape. */
  async function handleRestore() {
    if (id === undefined) {
      return;
    }
    setIsRestoring(true);
    setRestoreError(undefined);
    try {
      const restored = await apiFetch<VendorResource>(`/vendors/${id}/restore`, { method: "POST" });
      writeRecordToCache(restored);
      showToast(`${restored.full_name} is active again.`);
    } catch (error) {
      setRestoreError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsRestoring(false);
    }
  }

  const isArchived = recordQuery.data?.is_archived ?? false;

  const fields = useMemo<RecordField<VendorResource>[]>(() => {
    const textEdit = isOwner && !isArchived ? { edit: { kind: "text" as const } } : {};
    const contactMethodEdit =
      isOwner && !isArchived ? { edit: { kind: "select" as const, options: CONTACT_METHOD_OPTIONS } } : {};
    const tradeEdit = isOwner && !isArchived ? { edit: { kind: "select" as const, options: tradeOptions } } : {};

    return [
      // -- The fields that matter — render immediately (`RecordField.
      // primary`'s own default). Matches `VendorsList.tsx`'s own four list
      // columns exactly (name/trade/company/phone) — the same fields an
      // Owner is looking up a vendor for in the first place, mirroring
      // `TenantRecord.tsx`'s own "primary set mirrors what's most commonly
      // needed" shape. ------------------------------------------------------ //
      { key: "full_name", ...textEdit },
      { key: "mobile_phone", ...textEdit },
      { key: "trade", render: (row) => tradeLabelByValue[row.trade] ?? row.trade, ...tradeEdit },
      { key: "company_name", ...textEdit },
      // -- Everything else — behind "More details". ------------------------- //
      { key: "email", primary: false, ...textEdit },
      { key: "preferred_contact_method", primary: false, ...contactMethodEdit },
      { key: "notes", primary: false, ...textEdit },
    ];
  }, [isOwner, isArchived, tradeOptions, tradeLabelByValue]);

  const status: "loading" | "not-found" | "error" | "ready" = recordQuery.isPending
    ? "loading"
    : recordQuery.isError
      ? recordQuery.error instanceof ApiError && recordQuery.error.status === 404
        ? "not-found"
        : "error"
      : "ready";

  const errorMessage = recordQuery.error instanceof ApiError ? recordQuery.error.message : undefined;

  return (
    <div ref={contentRef} tabIndex={-1} className="max-w-record-column-max">
      <ReadOnlyNotice className="mb-4" />
      {restoreError ? (
        <p role="alert" className="mb-4 text-meta text-danger">
          {restoreError}
        </p>
      ) : null}
      {recordQuery.data ? (
        <ContactQuickLinks
          mobilePhone={recordQuery.data.mobile_phone}
          email={recordQuery.data.email}
          className="mb-4"
        />
      ) : null}
      <RecordPage<VendorResource>
        fields={fields}
        row={recordQuery.data}
        status={status}
        {...(errorMessage ? { errorMessage } : {})}
        identifier={(row) => row.full_name}
        backLink={{ label: "Vendors", href: "/vendors" }}
        icon={VendorsIcon}
        tint="cyan"
        {...(isArchived
          ? {
              primaryAction: {
                label: isRestoring ? "Restoring…" : "Restore",
                onClick: () => void handleRestore(),
                disabled: isRestoring,
              },
            }
          : {})}
        {...(!isArchived && isOwner && recordQuery.data
          ? {
              secondaryActions: [
                {
                  label: "Remove",
                  ariaLabel: `Remove ${recordQuery.data.full_name}`,
                  onClick: () => setRemoveOpen(true),
                },
              ],
            }
          : {})}
        onCommitEdit={commitEdit}
      />
      {recordQuery.data ? (
        <ConfirmDialog
          open={removeOpen}
          onOpenChange={(open) => {
            setRemoveOpen(open);
            if (!open) {
              setRemoveError(undefined);
            }
          }}
          title="Remove this vendor?"
          description={`Removing ${recordQuery.data.full_name} takes their record out of the active list right away. It can be restored for 30 days.`}
          confirmLabel={`Remove ${recordQuery.data.full_name}`}
          confirmingLabel="Removing…"
          isConfirming={isRemoving}
          onConfirm={() => void confirmRemove()}
          fallbackFocusRef={contentRef}
          {...(removeError ? { errorMessage: removeError } : {})}
        />
      ) : null}
    </div>
  );
}
